import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Home } from 'lucide-react';
import AnimatedText from '../components/AnimatedText';

const NotFoundPage: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';

  const translations = {
    en: {
      title: 'Lost in the Rankings?',
      subtitle: "We couldn't find what you're looking for",
      message: 'This page doesn\'t exist or may have been moved.',
      backToHome: 'Back to Home'
    },
    fr: {
      title: 'Perdu dans les Classements ?',
      subtitle: "Nous n'avons pas trouvé ce que vous cherchez",
      message: 'Cette page n\'existe pas ou a peut-être été déplacée.',
      backToHome: 'Retour à l\'Accueil'
    },
    de: {
      title: 'In den Rankings verloren?',
      subtitle: 'Wir konnten nicht finden, wonach du suchst',
      message: 'Diese Seite existiert nicht oder wurde möglicherweise verschoben.',
      backToHome: 'Zurück zur Startseite'
    },
    it: {
      title: 'Perso nelle Classifiche?',
      subtitle: 'Non siamo riusciti a trovare ciò che cerchi',
      message: 'Questa pagina non esiste o potrebbe essere stata spostata.',
      backToHome: 'Torna alla Home'
    }
  };

  const t = translations[currentLang] || translations.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 transition-colors duration-300">
      <div className="text-center max-w-2xl mx-auto">
        {/* Lottie Animation with glow effect */}
        <motion.div
          className="relative w-80 h-80 mx-auto mb-2"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Subtle glow behind animation */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(28, 122, 214, 0.08) 0%, transparent 70%)',
            }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.3, 0.5],
            }}
            transition={{
              duration: 3,
              ease: "easeInOut",
              repeat: Infinity
            }}
          />

          <DotLottieReact
            src="/404.lottie"
            loop
            autoplay
            className="relative z-10"
          />
        </motion.div>

        {/* Title with AnimatedText */}
        <div className="mb-4">
          <AnimatedText
            text={t.title}
            className="text-4xl md:text-5xl font-light text-gray-900 dark:text-gray-100 mb-2 transition-colors duration-300"
            delay={0.3}
            staggerDelay={0.02}
          />
        </div>

        {/* Subtitle */}
        <motion.p
          className="text-lg text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {t.subtitle}
        </motion.p>

        {/* Message */}
        <motion.p
          className="text-sm text-gray-500 dark:text-gray-400 mb-10 transition-colors duration-300"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          {t.message}
        </motion.p>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Link
            to={`/${currentLang}`}
            className="group inline-flex items-center gap-2 px-8 py-3 bg-[#1C7AD6] hover:bg-[#1565C0] text-white font-medium rounded-xl transition-all shadow-sm hover:shadow-md"
          >
            <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
            {t.backToHome}
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFoundPage;
